/**
 * Voice detail — sponsor, seats, membership, corpus preview.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, Mic2 } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { getItemCountState } from '../../../components/collection/types';
import { useSystemUsers } from '../../../hooks/useSystemUsers';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { useAgentsAsUsersEnabled } from '../../../features/voices/hooks/useAgentsAsUsersEnabled';
import { useVoicesCountedEnabled } from '../../../features/voices/hooks/useVoicesCountedEnabled';
import { VoiceBadge } from '../../../features/voices/components/VoiceBadge';
import { useVoicesStore } from '../../../features/voices/voicesStore';
import {
  getVoiceCalibrationSummary,
  type VoiceCalibrationSummary,
} from '../../../features/voices/voiceCalibrationService';
import type {
  VoiceCorpusDocument,
  VoiceCorpusKind,
  VoiceMembership,
  VoiceSeatType,
  VoiceStatus,
} from '../../../features/voices/types';

const SEAT_OPTIONS: VoiceSeatType[] = ['constituency', 'estimator', 'advocate', 'intake_submitter'];

/** Stable empty fallbacks — `?? []` in Zustand selectors creates a new array every snapshot and infinite-loops. */
const EMPTY_CORPUS: VoiceCorpusDocument[] = [];
const EMPTY_MEMBERSHIPS: VoiceMembership[] = [];

export default function VoiceDetailPage() {
  const enabled = useAgentsAsUsersEnabled();
  const countedEnabled = useVoicesCountedEnabled();
  const { voiceId } = useParams<{ voiceId: string }>();
  const fetchVoiceDetail = useVoicesStore((s) => s.fetchVoiceDetail);
  const fetchCorpus = useVoicesStore((s) => s.fetchCorpus);
  const saveVoice = useVoicesStore((s) => s.saveVoice);
  const reassignSponsor = useVoicesStore((s) => s.reassignSponsor);
  const saveMemberships = useVoicesStore((s) => s.saveMemberships);
  const voice = useVoicesStore((s) => s.voices.find((v) => v.id === voiceId));
  const corpus = useVoicesStore((s) =>
    voiceId ? (s.corpusByVoice[voiceId] ?? EMPTY_CORPUS) : EMPTY_CORPUS,
  );
  const memberships = useVoicesStore((s) =>
    voiceId ? (s.membershipsByVoice[voiceId] ?? EMPTY_MEMBERSHIPS) : EMPTY_MEMBERSHIPS,
  );
  const orgUnitsMap = useEnterpriseIdentityStore((s) => s.orgUnits);
  const orgUnits = useMemo(() => Array.from(orgUnitsMap.values()), [orgUnitsMap]);
  const fetchOrgUnits = useEnterpriseIdentityStore((s) => s.fetchFromSupabase);

  const { users } = useSystemUsers({ workspaceId: voice?.workspaceId ?? null });

  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [sponsorId, setSponsorId] = useState('');
  const [status, setStatus] = useState<VoiceStatus>('active');
  const [corpusKind, setCorpusKind] = useState<VoiceCorpusKind | ''>('');
  const [seats, setSeats] = useState<VoiceSeatType[]>(['constituency']);
  const [orgUnitIds, setOrgUnitIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [orgUnitSearch, setOrgUnitSearch] = useState('');
  const [calibration, setCalibration] = useState<VoiceCalibrationSummary | null>(null);

  useEffect(() => {
    if (enabled && voiceId) {
      void fetchVoiceDetail(voiceId);
      void fetchCorpus(voiceId);
    }
  }, [enabled, voiceId, fetchVoiceDetail, fetchCorpus]);

  // Phase 2: accuracy summary from the append-only calibration ledger.
  useEffect(() => {
    if (!enabled || !countedEnabled || !voiceId) return;
    let cancelled = false;
    void getVoiceCalibrationSummary(voiceId).then(({ data }) => {
      if (!cancelled) setCalibration(data);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, countedEnabled, voiceId]);

  const workspaceId = voice?.workspaceId;
  const formDisplayName = voice?.displayName;
  const formDescription = voice?.description ?? '';
  const formSponsorId = voice?.sponsorUserId;
  const formStatus = voice?.status;
  const formCorpusKind = voice?.corpusKind ?? '';
  const formSeatsKey = voice?.participationSeats.join(',') ?? '';

  useEffect(() => {
    if (workspaceId) void fetchOrgUnits();
  }, [workspaceId, fetchOrgUnits]);

  // Sync form from store when identity fields change — depend on primitives so
  // fetchVoiceDetail replacing the same row does not wipe in-progress edits.
  useEffect(() => {
    if (formDisplayName === undefined || formSponsorId === undefined || formStatus === undefined) {
      return;
    }
    setDisplayName(formDisplayName);
    setDescription(formDescription);
    setSponsorId(formSponsorId);
    setStatus(formStatus);
    setCorpusKind(formCorpusKind);
    setSeats(formSeatsKey ? (formSeatsKey.split(',') as VoiceSeatType[]) : ['constituency']);
  }, [
    formDisplayName,
    formDescription,
    formSponsorId,
    formStatus,
    formCorpusKind,
    formSeatsKey,
  ]);

  // Membership list reference is stable (EMPTY_MEMBERSHIPS or store-owned array).
  useEffect(() => {
    setOrgUnitIds(memberships.map((m) => m.orgUnitId));
  }, [memberships]);

  if (!enabled) {
    return <Navigate to="/prioritization/settings/agents" replace />;
  }

  if (!voiceId) {
    return <Navigate to="/prioritization/settings/voices" replace />;
  }

  if (!voice) {
    return (
      <div className="p-6">
        <p className="text-sm text-charcoal-500">Loading Voice…</p>
      </div>
    );
  }

  const corpusState = getItemCountState(corpus.length);
  const ouCountState = getItemCountState(orgUnits.length);
  const ouSearchQuery = orgUnitSearch.toLowerCase();
  const selectedOrgUnits = orgUnits.filter((ou) => orgUnitIds.includes(ou.id));
  const availableOrgUnits = orgUnits.filter(
    (ou) =>
      !orgUnitIds.includes(ou.id) &&
      (!orgUnitSearch || ou.name.toLowerCase().includes(ouSearchQuery)),
  );

  const toggleSeat = (seat: VoiceSeatType) => {
    setSeats((prev) => (prev.includes(seat) ? prev.filter((s) => s !== seat) : [...prev, seat]));
  };

  const toggleOrgUnit = (id: string) => {
    setOrgUnitIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    const saved = await saveVoice({
      id: voice.id,
      workspaceId: voice.workspaceId,
      displayName,
      description: description.trim() || null,
      sponsorUserId: sponsorId,
      participationSeats: seats,
      corpusKind: corpusKind || null,
      status,
      slug: voice.slug,
    });
    if (sponsorId !== voice.sponsorUserId) {
      await reassignSponsor(voice.id, sponsorId);
    }
    await saveMemberships(voice.id, orgUnitIds);
    setSaving(false);
    setMessage(saved ? 'Saved.' : 'Save failed.');
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Link
            to="/prioritization/settings/agents"
            className="inline-flex items-center gap-1 text-sm text-[var(--text-ter)] hover:text-[var(--text-pri)] no-underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Agents
          </Link>
          <span className="text-[var(--text-ter)]">/</span>
          <Link
            to="/prioritization/settings/voices"
            className="inline-flex items-center gap-1 text-sm text-primary-600 no-underline hover:underline"
          >
            Voices
          </Link>
        </div>
      </div>

      <PageHeader
        title={voice.displayName}
        subtitle="Voice identity — sponsor-owned, badged, context-only by default."
        actions={<VoiceBadge label={voice.badgeLabel} size="md" />}
      />

      {message && (
        <div className="border border-charcoal-200 bg-charcoal-50 px-3 py-2 text-sm text-charcoal-700">
          {message}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4 border border-charcoal-200 bg-[var(--bg-raised)] p-6">
          <h3 className="font-heading text-sm font-semibold text-charcoal-900">Identity</h3>
          <label className="block text-sm">
            <span className="font-medium text-charcoal-700">Display name</span>
            <input
              className="mt-1 w-full border border-charcoal-200 px-3 py-2 text-sm"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-charcoal-700">Description</span>
            <textarea
              className="mt-1 w-full border border-charcoal-200 px-3 py-2 text-sm"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-charcoal-700">Sponsor (required)</span>
            <select
              className="mt-1 w-full border border-charcoal-200 px-3 py-2 text-sm"
              value={sponsorId}
              onChange={(e) => setSponsorId(e.target.value)}
            >
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name ?? u.email}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-charcoal-500">
              Sponsor departure suspends this Voice immediately. Admins can reassign here.
            </p>
          </label>
          <label className="block text-sm">
            <span className="font-medium text-charcoal-700">Status</span>
            <select
              className="mt-1 w-full border border-charcoal-200 px-3 py-2 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value as VoiceStatus)}
            >
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="font-medium text-charcoal-700">Corpus kind</span>
            <select
              className="mt-1 w-full border border-charcoal-200 px-3 py-2 text-sm"
              value={corpusKind}
              onChange={(e) => setCorpusKind(e.target.value as VoiceCorpusKind | '')}
            >
              <option value="">None</option>
              <option value="support_tickets">Support tickets</option>
              <option value="crm_pipeline">CRM / sales pipeline</option>
            </select>
          </label>
        </div>

        <div className="space-y-4 border border-charcoal-200 bg-[var(--bg-raised)] p-6">
          <h3 className="font-heading text-sm font-semibold text-charcoal-900">Participation seats</h3>
          <p className="text-xs text-charcoal-500">
            Empty seats = worker-only (not invitable). Phase 1 uses constituency for context-only Voice input.
          </p>
          <div className="flex flex-wrap gap-2">
            {SEAT_OPTIONS.map((seat) => (
              <button
                key={seat}
                type="button"
                onClick={() => toggleSeat(seat)}
                className={` border px-2.5 py-1 text-xs font-medium ${
                  seats.includes(seat)
                    ? 'border-primary-400 bg-primary-50 text-primary-800'
                    : 'border-charcoal-200 text-charcoal-600'
                }`}
              >
                {seat}
              </button>
            ))}
          </div>

          <h3 className="pt-4 font-heading text-sm font-semibold text-charcoal-900">
            Membership (org units)
            {orgUnitIds.length > 0 && (
              <span className="ml-2 text-xs font-normal text-charcoal-500">
                {orgUnitIds.length} of {orgUnits.length}
              </span>
            )}
          </h3>
          {ouCountState === 'zero' ? (
            <p className="text-xs text-charcoal-500">No org units in this workspace.</p>
          ) : ouCountState === 'many' ? (
            <div className="space-y-2">
              <input
                type="search"
                placeholder="Search org units…"
                value={orgUnitSearch}
                onChange={(e) => setOrgUnitSearch(e.target.value)}
                className="w-full border border-charcoal-200 px-3 py-1.5 text-sm"
              />
              {selectedOrgUnits.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-charcoal-400">
                    Selected ({selectedOrgUnits.length})
                  </p>
                  <div className="max-h-32 space-y-1 overflow-y-auto">
                    {selectedOrgUnits.map((ou) => (
                      <label key={ou.id} className="flex items-center gap-2 text-sm text-charcoal-700">
                        <input type="checkbox" checked readOnly onChange={() => toggleOrgUnit(ou.id)} />
                        {ou.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-charcoal-400">
                  Available ({availableOrgUnits.length})
                </p>
                <div className="max-h-48 space-y-1 overflow-y-auto">
                  {availableOrgUnits.length === 0 ? (
                    <p className="text-xs text-charcoal-400">No org units match your search.</p>
                  ) : (
                    availableOrgUnits.map((ou) => (
                      <label key={ou.id} className="flex items-center gap-2 text-sm text-charcoal-700">
                        <input type="checkbox" checked={false} onChange={() => toggleOrgUnit(ou.id)} />
                        {ou.name}
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="max-h-48 space-y-1 overflow-y-auto">
              {orgUnits.map((ou) => (
                <label key={ou.id} className="flex items-center gap-2 text-sm text-charcoal-700">
                  <input
                    type="checkbox"
                    checked={orgUnitIds.includes(ou.id)}
                    onChange={() => toggleOrgUnit(ou.id)}
                  />
                  {ou.name}
                </label>
              ))}
            </div>
          )}

          <p className="text-xs text-charcoal-500">
            Default counting policy: <strong>Advisory</strong> — the permanent floor.
            {countedEnabled
              ? ' Facilitators can opt an estimator seat into Counted per session (logged).'
              : ''}
          </p>
        </div>
      </div>

      {countedEnabled && (
        <div className="border border-charcoal-200 bg-[var(--bg-raised)] p-6">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-heading text-sm font-semibold text-charcoal-900">
              Calibration track record
            </h3>
            <span className="text-xs text-charcoal-500">append-only ledger</span>
          </div>
          {!calibration || calibration.count === 0 ? (
            <p className="text-sm text-charcoal-500">
              No calibration data yet. Counted estimator responses are compared against
              published session results after each session publish — the Voice earns
              trust by being visibly right.
            </p>
          ) : (
            <div className="flex flex-wrap gap-8">
              <div>
                <p className="text-2xl font-semibold text-charcoal-900">{calibration.count}</p>
                <p className="text-xs text-charcoal-500">estimates scored</p>
              </div>
              <div>
                <p className="text-2xl font-semibold text-charcoal-900">
                  {calibration.meanAbsError != null ? calibration.meanAbsError.toFixed(1) : '—'}
                </p>
                <p className="text-xs text-charcoal-500">mean absolute error vs. published results</p>
              </div>
              <div>
                <p className="text-2xl font-semibold text-charcoal-900">{calibration.sessionCount}</p>
                <p className="text-xs text-charcoal-500">published sessions</p>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="border border-charcoal-200 bg-[var(--bg-raised)] p-6">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-heading text-sm font-semibold text-charcoal-900">Corpus documents</h3>
          <span className="text-xs text-charcoal-500">{corpus.length} docs</span>
        </div>
        {corpusState === 'zero' ? (
          <p className="text-sm text-charcoal-500">
            No corpus yet. Seeded Voices include support tickets or CRM rows. Live connector ingest is Phase 2+.
          </p>
        ) : (
          <ul className="divide-y divide-charcoal-100">
            {(corpusState === 'many' ? corpus.slice(0, 20) : corpus).map((doc) => (
              <li key={doc.id} className="py-2">
                <div className="flex items-center gap-2">
                  <Mic2 className="h-3.5 w-3.5 text-electric-mustard-600" />
                  <span className="text-sm font-medium text-charcoal-900">{doc.title}</span>
                  {doc.externalRef && (
                    <code className="font-mono text-sm text-charcoal-500">{doc.externalRef}</code>
                  )}
                </div>
                <p className="mt-0.5 line-clamp-2 text-xs text-charcoal-500">{doc.body}</p>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          disabled={saving || !displayName.trim() || !sponsorId}
          onClick={() => void handleSave()}
          className="bg-primary-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save Voice'}
        </button>
      </div>
    </div>
  );
}
